import isEqual from 'lodash/isEqual'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'

import type { ComposerStore } from '../composerStore'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'

export interface FluxToProfileFieldsMirror {
  unsubscribe: () => void
}

/**
 * Register an `AppStore` change listener that mirrors mutable per-profile
 * fields — `selectedSubprofileId` and `subprofiles` — into Zustand's
 * `channelSlice.profiles`.
 *
 * INC-470 root cause: `channelSlice.profiles` is seeded once, synchronously,
 * from the raw `Profile[]` prop at composer mount (`ComposerProvider.tsx`,
 * via `DataImportUtils.formatProfilesData`) — a completely separate object
 * graph from Flux's own live `AppStore` profiles, with no ongoing sync at
 * all. `PinterestBoardSelector.tsx`'s board selection writes
 * `AppActionCreators.selectSubprofile` → `profile.selectedSubprofileId =
 * ...` directly on the Flux-side profile; nothing re-synced that into
 * Zustand, so `useSelectedProfiles()` (read at save time when
 * `composer-zustand-mode-state` is on) saw the mount-time value.
 *
 * Scope: `selectedSubprofileId` + `subprofiles` are the two fields actually
 * read through the Zustand profile path today (both feed
 * `selectedBoardServiceId()` in `useGraphCallbacks.ts`). Other per-profile
 * fields Flux mutates post-seed (`isSelected`, `shorteningDomain`,
 * `subprofilesOrignatedFromAPI`) have no current Zustand-side reader — add
 * them here if a future consumer starts reading them off a Zustand-sourced
 * profile.
 *
 * This is a value mirror, not an identity mirror: `ComposerProfile` is plain
 * data (unlike `editorState`'s live `BufferEditor`), so a structural field
 * copy is correct and Immer-safe.
 */
export function installFluxToProfileFieldsMirror(
  store: ComposerStore,
): FluxToProfileFieldsMirror {
  const syncProfileFields = (): void => {
    runMirrorWrite(() => {
      const state = store.getState()
      for (const profileId of Object.keys(state.profiles)) {
        const fluxProfile = AppStore.getProfile(profileId)
        if (!fluxProfile) continue

        const zustandProfile = state.profiles[profileId]
        const subprofileIdChanged =
          zustandProfile.selectedSubprofileId !==
          fluxProfile.selectedSubprofileId
        const subprofilesChanged = !isEqual(
          zustandProfile.subprofiles,
          fluxProfile.subprofiles,
        )

        if (!subprofileIdChanged && !subprofilesChanged) continue

        state.updateProfileFields(profileId, {
          selectedSubprofileId: fluxProfile.selectedSubprofileId,
          // Cloned, not the live Flux array: the Zustand store is
          // immer/auto-freeze, and Flux mutates this array in place
          // (AppStore.ts's createSubprofile pushes a newly-created board
          // onto it) — writing the shared reference would freeze it and
          // break that in-place push with a TypeError.
          subprofiles: fluxProfile.subprofiles.map((subprofile) =>
            Object.assign({}, subprofile),
          ),
        })
      }
    })
  }

  const handleChange = (): void => {
    if (isMirrorWriteInProgress()) return
    syncProfileFields()
  }

  AppStore.addChangeListener(handleChange)
  // Catches drift that landed on Flux between Zustand's mount-time seed and
  // this mirror installing — e.g. init-time board auto-select can complete
  // before this effect runs — rather than relying on the next AppStore
  // emission to self-heal it.
  syncProfileFields()

  return {
    unsubscribe: (): void => {
      AppStore.removeChangeListener(handleChange)
    },
  }
}
